import { Search } from "lucide-react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";
import { useGlobalSearch } from "@/contexts/GlobalSearchContext";

export interface HeaderSearchProps {
  className?: string;
}

/**
 * Header search — a trigger, not a field of its own.
 *
 * It used to be a real input that answered one question (topics) and jumped
 * straight to Browse. That made it a different search from the one in the
 * sidebar, with a different set of destinations and its own ⌘K handler
 * competing with every other one on the page. Now both surfaces open the same
 * palette, so "search" means the same thing everywhere and the shortcut is
 * registered in exactly one place.
 *
 * Rendered as a button that *looks* like a field: the control's job here is to
 * announce "search is here", and the typing happens where the results do.
 */
export const HeaderSearch = ({ className }: HeaderSearchProps) => {
  const { t } = useTranslation();
  const { openSearch } = useGlobalSearch();
  const label = t("nav.search.open", "Search topics and pronunciation");

  return (
    <button
      type="button"
      onClick={() => openSearch()}
      aria-label={label}
      aria-keyshortcuts="Meta+K Control+K"
      data-tour="header-search"
      className={cn(
        "group relative flex h-8 w-full items-center gap-2 rounded-lg border border-stone-200/70 bg-stone-50/80 pl-8 pr-2 text-left text-sm",
        "text-muted-foreground transition-colors hover:border-stone-300 hover:bg-stone-100/80",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30",
        "dark:border-stone-700/70 dark:bg-stone-800/40 dark:hover:border-stone-600 dark:hover:bg-stone-800/70",
        "md:w-[140px] lg:w-[240px]",
        className,
      )}
    >
      <Search
        className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors group-hover:text-primary"
        aria-hidden
      />
      <span className="truncate">{t("nav.search.trigger", "Search")}</span>
      <kbd className="ml-auto hidden shrink-0 items-center gap-0.5 rounded border bg-white px-1.5 font-mono text-[11px] font-medium text-muted-foreground md:inline-flex dark:border-stone-700 dark:bg-stone-900">
        <span className="text-xs">⌘</span>K
      </kbd>
    </button>
  );
};

export default HeaderSearch;
